import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, Observable, take } from 'rxjs';
import { routerSegments } from '@consts/routes';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import type { ActiveOrganization } from '@models/organization.model';
import { AppState } from '../store/app.state';

/**
 * Keeps walls pages aligned with the org `useWallV3Page` flag:
 * `/wall-v2/**` redirects to `/wall-v3/**` while the flag is on, and
 * `/wall-v3/**` falls back to `/wall-v2/**` while it is off.
 * Waits for `activeOrganization` so an empty store is not treated as "flag off".
 */
export const wallV3PageRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
): Observable<boolean | UrlTree> => {
  const store = inject<Store<AppState>>(Store);
  const router = inject(Router);
  const isV3Route: boolean = route.routeConfig?.path === routerSegments.wallV3;

  return store.select(OrganizationSelectors.selectActiveOrganization).pipe(
    filter((activeOrganization): activeOrganization is ActiveOrganization => activeOrganization != null),
    take(1),
    map((activeOrganization) => {
      const useWallV3Page: boolean = activeOrganization.useWallV3Page ?? false;

      if (useWallV3Page === isV3Route) {
        return true;
      }

      const targetSegment: string = useWallV3Page ? routerSegments.wallV3 : routerSegments.wallV2;
      const parsed = router.parseUrl(router.url);
      const primarySegments: string[] =
        parsed.root.children['primary']?.segments.map((segment) => segment.path) ?? [];
      const pathAfterWall: string[] = primarySegments.slice(1);

      return router.createUrlTree(['/', targetSegment, ...pathAfterWall], {
        queryParams: parsed.queryParams,
      });
    }),
  );
};
